import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Boxes as BoxesIcon, Search } from "lucide-react";

const CATEGORIES = ["Gemstones","Rudraksha","Bracelets","Crystals","Yantras","Books","Vastu Products"];

export default function Products() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Gemstones", sku: "", description: "", purchase_price: 0, selling_price: 0, stock: 0, supplier: "" });

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (cat !== "all") params.category = cat;
    try {
      const { data } = await api.get("/products", { params });
      setItems(data);
    } catch (err) { console.error("products load failed:", err?.message); }
  }, [q, cat]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/products", form);
    toast.success("Product added");
    setOpen(false); load();
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Inventory</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1">Products</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#1E3A8A] btn-pill h-11 px-5" data-testid="new-product-btn"><Plus className="w-4 h-4 mr-1.5" /> New product</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">Add product</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div className="col-span-2"><Field label="Name"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></Field></div>
              <Field label="Category">
                <Select value={form.category} onValueChange={v=>setForm({...form,category:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="SKU"><Input value={form.sku} onChange={e=>setForm({...form,sku:e.target.value})} /></Field>
              <Field label="Purchase price (₹)"><Input type="number" value={form.purchase_price} onChange={e=>setForm({...form,purchase_price:parseFloat(e.target.value||0)})} /></Field>
              <Field label="Selling price (₹)"><Input type="number" value={form.selling_price} onChange={e=>setForm({...form,selling_price:parseFloat(e.target.value||0)})} /></Field>
              <Field label="Stock"><Input type="number" value={form.stock} onChange={e=>setForm({...form,stock:parseInt(e.target.value||0)})} /></Field>
              <Field label="Supplier"><Input value={form.supplier} onChange={e=>setForm({...form,supplier:e.target.value})} /></Field>
              <div className="col-span-2"><Field label="Description"><Textarea rows={3} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} /></Field></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#1E3A8A] btn-pill">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search by name or SKU…" className="pl-9 h-11 rounded-xl" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-48 h-11 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 rounded-2xl text-center border-dashed">
          <BoxesIcon className="w-10 h-10 mx-auto text-slate-400" strokeWidth={1.25} />
          <p className="mt-3 text-slate-500">No products in inventory yet.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(p => (
            <Card key={p.id} className="p-5 rounded-2xl card-lift" data-testid={`product-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <Badge variant="secondary" className="text-[10px]">{p.category}</Badge>
                  <div className="font-display text-lg mt-2">{p.name}</div>
                  <div className="text-xs font-mono text-slate-500">{p.sku}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Stock</div>
                  <div className="font-display text-xl">{p.stock}</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <div className="text-xs text-slate-500">Cost ₹{p.purchase_price}</div>
                <div className="font-display text-lg text-amber-600">₹{p.selling_price}</div>
              </div>
              {p.description && <div className="mt-2 text-xs text-slate-500 line-clamp-2">{p.description}</div>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>);
